import { DirectUploadColumnHeaders, FirstPartyColumnHeaders } from "../../../types";
const LIVERAMP_CSV_COLUMNS = Object.values(FirstPartyColumnHeaders);
/**
 * UUID/MAID validation using regex.
 * Accepts any string in UUID format (8-4-4-4-12 hex pattern).
 * Does not enforce version or variant bits.
 */
const isValidUUID = (value) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(value);
};
/**
 * Browser-compatible IPv4 validation using regex.
 * Validates that each octet is 0-255.
 */
const isValidIPv4 = (value) => {
    const ipv4Regex = /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    return ipv4Regex.test(value);
};
/**
 * Browser-compatible IPv6 validation using regex.
 * Supports full, compressed, and mixed IPv6 formats.
 */
const isValidIPv6 = (value) => {
    // Comprehensive IPv6 regex that handles:
    // - Full format: 2001:0db8:85a3:0000:0000:8a2e:0370:7334
    // - Compressed format: 2001:0db8:85a3::8a2e:0370:7334
    // - IPv4-mapped IPv6: ::ffff:192.0.2.1
    const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
    return ipv6Regex.test(value);
};
export const validators = {
    /**
     * Validates a single row of LiveRamp PII data by validating row value combos as well as regex rules for each column in the row.
     * @param pii - the pii object to validate - unused in this case.
     * @param rowData - the row data to validate.
     * @returns either a string containing aggregated row errors or undefined if no error.
     */
    liveRampRow: (pii, rowData) => {
        const { columnHeaders, rowIndex, row } = rowData;
        const colLength = columnHeaders.length;
        const errors = [];
        const values = {};
        // Build a map of column name to value for easier access.
        columnHeaders.map((columnName, colIndex) => {
            /* @ts-expect-error - (TS Upgrade: 5.7.3) - https://typescript.tv/errors/#ts7053 */
            values[columnName] = row[colIndex];
        });
        // Destructure the minimum required columns for combo validation.
        const { email, phone, name, firstName, lastName, zipCode } = values;
        // Validate combinations of columns, according the following (LiveRamp) rules
        // https://developers.liveramp.com/rampid-api/reference/batch-request-calls-match#match-parameter-combinations
        const hasName = name || (firstName && lastName);
        const hasEmailOrPhone = email || phone;
        if (!hasEmailOrPhone && !hasName) {
            errors.push("Email or Phone required.");
        }
        else if (hasName) {
            if (!(email || phone || zipCode)) {
                errors.push("Email, Phone, or Zip Code required.");
            }
        }
        else if (!hasEmailOrPhone) {
            errors.push("Must include either name or the parsed name (firstName, lastName).");
        }
        // If there isn't a valid combination of columns, we don't need to validate the individual columns
        // so return early with the errors.
        if (errors.length > 0) {
            return `Row #${rowIndex}: ${errors.join(" ")}`;
        }
        // Validate each column individually (ie. regex, length, etc.)
        for (let colIndex = 0; colIndex < colLength; colIndex++) {
            const columnName = columnHeaders[colIndex];
            const columnData = {
                columnName,
                value: row[colIndex],
                colIndex
            };
            // At this point, we are only validating columns that have values. The logic above
            // should have already verified that the row has the required columns and that the
            // columns have values.
            const error = !!columnData.value && validateLiveRampColumn(columnData);
            error && errors.push(error);
        }
        return errors.length > 0 ? `Row #${rowIndex}: ${errors.join(" ")}` : undefined;
    },
    /**
     * Validates a single row of Direct 1P Upload data.
     * Direct 1P requires 'identifier' field, 'hashed_email' is optional.
     * Validates headers first, then individual column values.
     */
    directRow: (pii, rowData) => {
        const { columnHeaders, rowIndex, row } = rowData;
        const colLength = columnHeaders.length;
        const errors = [];
        // Validate column headers (only on first row to avoid redundant checks)
        // Note: This validation happens per-row in chunked processing, but the header
        // structure remains constant across all rows, so the validation result is the same
        const headerError = validateDirectUploadHeaders(columnHeaders);
        if (headerError) {
            // Header errors apply to the entire file, not just this row
            return headerError;
        }
        // Build column map
        const values = {};
        columnHeaders.map((columnName, colIndex) => {
            values[columnName] = row[colIndex];
        });
        // Validate required fields
        const identifier = values[DirectUploadColumnHeaders.IDENTIFIER];
        if (!identifier || identifier.trim() === "") {
            errors.push("Identifier is required.");
        }
        // Return early if required field missing
        if (errors.length > 0) {
            return `Row #${rowIndex}: ${errors.join(" ")}`;
        }
        // Validate each column individually using Direct Upload validator
        // Note: We always validate, even for empty values, to catch unrecognized columns
        for (let colIndex = 0; colIndex < colLength; colIndex++) {
            const columnName = columnHeaders[colIndex];
            const columnData = {
                columnName,
                value: row[colIndex],
                colIndex
            };
            const error = validateDirectUploadColumn(columnData);
            error && errors.push(error);
        }
        return errors.length > 0 ? `Row #${rowIndex}: ${errors.join(" ")}` : undefined;
    },
    /**
     * Name shouldn't be undefined or empty
     */
    name: (pii, value) => {
        if (value === undefined || value === "") {
            return "Name is required";
        }
        return;
    }
};
/**
 * Validates a single column of LiveRamp PII data by running a regex against the column's value depending on the column name.
 * Regex patterns are aligned with backend validation in services/segment-pii-ingest to ensure consistent
 * validation across frontend and backend systems.
 * @param columnData the column data (ie. columnName, value) to validate.
 * @returns either a string containing an error message or undefined if no error.
 */
const validateLiveRampColumn = (columnData) => {
    const { columnName, value } = columnData;
    switch (columnName) {
        case FirstPartyColumnHeaders.STATE:
            if (!/^[A-Z]{2}$/.test(value)) {
                return "State needs to be an uppercase two-character state abbreviation.";
            }
            break;
        case FirstPartyColumnHeaders.ZIP_CODE:
            if (!/^\d{5}(?:-\d{4}(?:-\d{2})?)?$/.test(value)) {
                return "Zip code needs to be 5, 9, or 11 digits, hyphen required when 9 or 11 digits.";
            }
            break;
        case FirstPartyColumnHeaders.EMAIL:
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                return "Invalid email address.";
            }
            break;
        case FirstPartyColumnHeaders.PHONE:
            if (!/^\d{10}$/.test(value)) {
                return "Phone needs to be 10 digits with no special characters.";
            }
            break;
        case FirstPartyColumnHeaders.PRIMARY_NUMBER:
            if (!/^[0-9]+$/.test(value)) {
                return "Primary number must be a numeric value.";
            }
            break;
        case FirstPartyColumnHeaders.NAME:
            // \u00C0-\u00FF matches accented characters (À-ÿ) for international names like "José García"
            if (!/^([A-Za-z\u00C0-\u00FF'-]+)( [A-Za-z\u00C0-\u00FF]{1})?( [A-Za-z\u00C0-\u00FF'-]{2,}){1,2}$/.test(value.trim())) {
                return "Name must be a full name with at least a first and last name.";
            }
            break;
        case FirstPartyColumnHeaders.STREET_ADDRESS:
            if (!/^\S+(?:\s+\S+){2}/.test(value)) {
                return "Street Address must be a complete street address (postal address without city, state, zip).";
            }
            break;
        default:
            if (LIVERAMP_CSV_COLUMNS.indexOf(columnName) === -1) {
                return `Unrecognized column name: ${columnName}`;
            }
            break;
    }
    return undefined;
};
/**
 * Validates that Direct Upload CSV contains only the allowed column headers.
 * Must contain 'identifier', may optionally contain 'hashed_email'.
 * Any other columns are rejected.
 * @param columnHeaders - Array of column header names from the CSV
 * @returns Error message or undefined if valid
 */
const validateDirectUploadHeaders = (columnHeaders) => {
    const ALLOWED_COLUMNS = Object.values(DirectUploadColumnHeaders);
    const unrecognizedColumns = [];
    // Check for unrecognized columns
    columnHeaders.forEach((header) => {
        if (!ALLOWED_COLUMNS.includes(header)) {
            unrecognizedColumns.push(header);
        }
    });
    if (unrecognizedColumns.length > 0) {
        return `Unrecognized column(s) for Direct Upload: ${unrecognizedColumns.join(", ")}. Only 'identifier' and 'hashed_email' are allowed.`;
    }
    // Check for required 'identifier' column
    if (!columnHeaders.includes(DirectUploadColumnHeaders.IDENTIFIER)) {
        return "Missing required column 'identifier' for Direct Upload.";
    }
    return undefined;
};
/**
 * Validates a single column for Direct 1P Upload.
 * Only validates 'identifier' and 'hashed_email' columns.
 * @param columnData - Column name and value to validate
 * @returns Error message or undefined if valid
 */
const validateDirectUploadColumn = (columnData) => {
    const { columnName, value } = columnData;
    switch (columnName) {
        case DirectUploadColumnHeaders.IDENTIFIER:
            // Skip value validation if empty (required field check happens at row level)
            if (!value || value.trim() === "") {
                return undefined;
            }
            // Check if it's a valid IP address (IPv4 or IPv6) using browser-compatible validators
            const isIPv4 = isValidIPv4(value);
            const isIPv6 = isValidIPv6(value);
            // Check if it's a valid UUID/MAID format using regex
            const isUUID = isValidUUID(value);
            if (!isIPv4 && !isIPv6 && !isUUID) {
                return "Identifier must be a valid IPv4 address, IPv6 address, or MAID (UUID format).";
            }
            break;
        case DirectUploadColumnHeaders.HASHED_EMAIL:
            // Skip validation if empty (hashed_email is optional)
            if (!value || value.trim() === "") {
                return undefined;
            }
            // Reject plain email addresses (reuse email pattern from LiveRamp validator)
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (emailRegex.test(value)) {
                return "Hashed email must not be a plain email address. It should be a SHA256 hash.";
            }
            // Require SHA256 hash format (64 hex characters, case-insensitive)
            if (!/^[a-f0-9]{64}$/i.test(value)) {
                return "Hashed email must be a valid SHA256 hash (64 hexadecimal characters).";
            }
            break;
        default:
            // This should never be reached if validateDirectUploadHeaders is called first,
            // but kept as a safety check
            return `Unrecognized column name for Direct Upload: ${columnName}`;
    }
    return undefined;
};
//# sourceMappingURL=index.js.map